import React, {
  useState,
  HTMLAttributes,
  useRef,
  useImperativeHandle,
  forwardRef,
  ComponentPropsWithRef,
} from "react";
import cx from "classnames";
import PropTypes from "prop-types";
import { VideoButton } from "../VideoButton";
import { ReactDefaultProps } from "../../types/react";

import styles from "./ProductImage.scss";

type VideoSlideBaseProps = {
  src: string;
  customClass?: string;
  active?: boolean;
};

export type VideoSlideProps = VideoSlideBaseProps &
  Omit<HTMLAttributes<HTMLVideoElement>, "src">;

const propTypes = {
  src: PropTypes.string.isRequired,
  customClass: PropTypes.string,
  active: PropTypes.bool,
} as const;

const defaultProps = {
  customClass: undefined,
  active: false,
} as const;

const validatedDefaultProps: ReactDefaultProps<
  VideoSlideBaseProps,
  typeof defaultProps
> = defaultProps;

const VideoSlideWithRef = (
  props: VideoSlideProps,
  forwardedRef: ComponentPropsWithRef<"video">["ref"]
): JSX.Element => {
  const [wasStartedOnce, setWasStartedOnce] = useState(false);
  const { src, customClass, active, ...restProps } = props;
  const videoRef = useRef<HTMLVideoElement>(null);

  useImperativeHandle(forwardedRef, () => videoRef.current as HTMLVideoElement);

  const handlePlayButtonClick = () => {
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play();
      }
    }
  };
  return (
    <div className={styles["video-wrapper"]}>
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <video
        ref={videoRef}
        src={src}
        onPlay={() => setWasStartedOnce(true)}
        className={cx(styles.video, customClass)}
        preload="none"
        controls={wasStartedOnce}
        {...restProps}
      />
      {!wasStartedOnce && (
        <VideoButton
          customClass={styles["video-button"]}
          onClick={handlePlayButtonClick}
        />
      )}
    </div>
  );
};

export const VideoSlide = forwardRef(VideoSlideWithRef);

VideoSlide.displayName = "ProductImageVideoSlide";

VideoSlide.propTypes = propTypes;
VideoSlide.defaultProps = validatedDefaultProps;

export default VideoSlide;
